import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbChange} from '@features/admin-panel/helpers/admin-dashboard-format';
import {adbCategoryAxis, adbChartTheme, adbGrid, adbLegend, adbPartialItem, adbRedrawOnThemeChange, adbTodaySoFar, adbTooltip, adbValueAxis, adbZoom} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdminDashboardLoginsPerDay,
  AdminDashboardQueryParams,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView, AdminDashboardLoginsDayDrill} from '@features/admin-panel/models/admin-dashboard-drill.models';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const loginsPerDayCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** The period as the subtitle says it. */
const PERIOD_LONG: {[key: string]: string} = {'7d': '7 days', '30d': '30 days', '90d': '90 days', '12m': '12 months'};

const day = (iso: string): string => formatDate(iso, 'd MMM', 'en-US', 'UTC');
const int = (value: number): string => formatNumber(value ?? 0, 'en-US', '1.0-0');

/**
 * Users & adoption "Logins per day": this period's daily logins against the previous period, drawn dashed and
 * shifted by whole weeks so a Monday meets a Monday. The subtitle compares the totals of the two true windows.
 * Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-logins-per-day',
  templateUrl: './admin-dashboard-logins-per-day.component.html',
  styleUrls: ['./admin-dashboard-logins-per-day.component.scss']
})
export class AdminDashboardLoginsPerDayComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** "Evidence" in a day's drill: the dashboard opens that company's why panel (ITP-E-159-05). */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly failedTitle = 'Couldn\'t load logins per day';

  data: AdminDashboardLoginsPerDay | null = null;
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>, private drill: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('users-adoption', this.host.nativeElement, this);
  }

  /** The chart container exists only while there are logins to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = loginsPerDayCharts.init(this.chartElement);
      this.chart.on('click', (event: any) => this.openDay(event?.dataIndex));
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-09). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: 'Logins per day', text: this.failedTitle}];
    }
    const block: AdminDashboardReportBlock = this.hasLogins
      ? {
        kind: 'chart',
        title: 'Logins per day',
        subtitle: this.subtitle,
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF', excludeComponents: ['dataZoom']}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      }
      : {kind: 'empty', title: 'Logins per day', text: 'No logins in this view. Widen the filters or the period.'};
    if (this.data?.historyFrom) {
      block.note = `Collecting history — trend available from ${formatDate(this.data.historyFrom, 'd MMM y', 'en-US', 'UTC')}`;
    }
    return [block];
  }

  get hasLogins(): boolean {
    return (this.data?.total ?? 0) > 0;
  }

  get periodLong(): string {
    return PERIOD_LONG[this.params?.period ?? '30d'] ?? PERIOD_LONG['30d'];
  }

  /** "7 logins in the last 30 days · ▼ 12.5% vs the 30 days before · busiest day 6 Oct (5) · 4 today so far · −20% vs this time yesterday" */
  get subtitle(): string {
    const peak = this.data?.peak;
    const busiest = peak ? ` · busiest day ${day(peak.date)} (${int(peak.value)})` : '';
    return `${countOf(this.data?.total ?? 0, 'login')} in the last ${this.periodLong} · ${this.changeText} vs the ${this.periodLong} before${busiest}${this.todayText}`;
  }

  /**
   * " · 412 today so far · +8% vs this time yesterday", or " · 412 today so far · none by this time yesterday" when
   * yesterday had no logins by this time (ITP-E-211-04, Plan ITP-P-50 Q11); '' from an older server without `today`.
   */
  get todayText(): string {
    const today = this.data?.today;
    if (!today) {
      return '';
    }
    const versus = today.sameTimeYesterday
      ? `${adbChange(today.soFar ?? 0, today.sameTimeYesterday)} vs this time yesterday`
      : 'none by this time yesterday';
    return ` · ${int(today.soFar)} today so far · ${versus}`;
  }

  /** "▲ 12.5%" / "▼ 12.5%" against the previous total, or "no comparison" when the previous period had none. */
  get changeText(): string {
    const current = this.data?.total ?? 0;
    const previous = this.data?.previousTotal ?? 0;
    if (!previous) {
      return 'no comparison';
    }
    const change = (current - previous) / previous;
    return `${change >= 0 ? '▲' : '▼'} ${(Math.abs(change) * 100).toFixed(1)}%`;
  }

  /**
   * "6 Oct: 5 logins<br/>1 Sep: 7 logins" — a day against the same weekday of the previous period. Today's live point
   * says "8 Oct, today so far (14:20 UTC): 412 logins" (ITP-E-211-04).
   */
  tooltipText(index: number): string {
    const current = this.data?.current?.[index];
    const previous = this.data?.previous?.[index];
    if (!current) {
      return '';
    }
    const asOf = current.partial ? this.data?.today?.asOf : null;
    const label = asOf ? `${day(current.date)}, ${adbTodaySoFar(asOf)}` : day(current.date);
    const line = `${label}: ${countOf(current.value, 'login')}`;
    return previous ? `${line}<br/>${day(previous.date)}: ${countOf(previous.value, 'login')}` : line;
  }

  /**
   * A day was clicked (F4; OCA-E-159 DetailSections.tsx:80-96): the drill lists the companies that logged in that day,
   * the top 25 by logins against each company's 4-week daily average. Evidence there opens the why panel.
   */
  openDay(index: number): void {
    const point = this.data?.current?.[index ?? -1];
    if (!point || !this.params) {
      return;
    }
    const params = this.params;
    const key = formatDate(point.date, 'yyyy-MM-dd', 'en-US', 'UTC');
    this.drill.open({
      title: `Who logged in on ${formatDate(point.date, 'd MMM yyyy', 'en-US', 'UTC')}`,
      columns: [{header: 'Logins', align: 'end'}, {header: '4-wk daily avg', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardLoginsDayDrill>('AdminDashboardLoginsDayDrillQuery', params, {Day: key}).pipe(
        map((response): AdminDashboardDrillView => ({
          subtitle: `${countOf(response?.TotalLogins ?? 0, 'login')} across ${countOf(response?.Total ?? 0, 'company', 'companies')}. Top 25 by logins, against each company's 4-week daily average.`,
          rows: (response?.Rows ?? []).map(row => adbDrillRowView(row, [{text: int(row.logins)}, {text: (row.avgDaily28 ?? 0).toFixed(1)}])),
          total: response?.Total ?? 0
        }))
      )
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardLoginsPerDay>(
      'AdminDashboardLoginsPerDayQuery', this.params, 'LoginsPerDay'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardLoginsPerDay | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.hasLogins ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.data) {
      return;
    }
    const current = this.data.current ?? [];
    const previous = this.data.previous ?? [];
    // OCA-E-159 loginsOption (charts.ts:151-193): themed axes and legend, an inside zoom plus a slider, this period
    // a blue line over a pale area, the previous period dashed in ink 2. Colours come from the card's --adb-* tokens.
    const t = adbChartTheme(this.host.nativeElement);
    // ITP-E-211-04. Today's live point (partial) is drawn apart: this period's line stops at yesterday, and a dashed
    // link leads from yesterday to a faded point. It is not in the legend.
    const last = current.length - 1;
    const partial = last >= 0 && current[last]?.partial === true;
    const series: Record<string, unknown>[] = [
      {
        name: 'This period', type: 'line', data: current.map((p, i) => partial && i === last ? null : p.value), showSymbol: false, symbolSize: 6,
        lineStyle: {width: 2, color: t.blue}, itemStyle: {color: t.blue},
        areaStyle: {color: t.blue, opacity: 0.08}, emphasis: {focus: 'series'}
      },
      {
        name: 'Previous period, same weekdays', type: 'line', data: previous.map(p => p.value), showSymbol: false,
        lineStyle: {width: 1.25, type: 'dashed', color: t.ink2}, itemStyle: {color: t.ink2}
      }
    ];
    if (partial) {
      series.push({
        name: 'Today so far', type: 'line',
        data: current.map((p, i) => i === last
          ? {value: p.value, symbol: 'circle', symbolSize: 7, itemStyle: adbPartialItem(t, t.blue)}
          : i === last - 1 ? {value: p.value, symbol: 'none'} : null),
        showSymbol: true, showAllSymbol: true,
        lineStyle: {width: 2, type: 'dashed', color: t.blue}, itemStyle: {color: t.blue}
      });
    }
    const option = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid({bottom: 52}),
      legend: {...adbLegend(t), data: ['This period', 'Previous period, same weekdays']},
      xAxis: adbCategoryAxis(t, current.map(p => day(p.date)), {boundaryGap: false}),
      yAxis: adbValueAxis(t, (value: number) => int(value), {minInterval: 1}),
      dataZoom: adbZoom(t),
      tooltip: adbTooltip(t, {
        formatter: (points: any) => `${this.tooltipText((Array.isArray(points) ? points[0] : points)?.dataIndex ?? 0)}<br/><span style="color:${t.ink2}">Click to see who logged in</span>`
      }),
      series
    };
    this.chart.setOption(option, true);
  }
}
